import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Eye, EyeOff, Minus, Plus, Smartphone, Star } from 'lucide-react';

import PageCard from '../../PageCard/PageCard';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { cn } from '../../../lib/utils';
import { CategoryGroup, CompanyRole, RoleGrant, SharedCategory } from '../../../model/team.model';
import {
  categories as initialCategories,
  categoryGroups as initialGroups,
  formatHours,
  grantedLeaves,
  members,
  memberCategoryTotals,
  memberRoles,
  roleGrants as initialGrants,
  roles as initialRoles,
  rolesOf,
} from './teamMock';

type CellState = 'granted' | 'inherited' | 'implied' | 'off';

const SWATCHES = ['#4d7ab4', '#b4674d', '#7a5cb4', '#b4a14d', '#4db4a1', '#6b7a5c', '#8a8580', '#a0787a'];

const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<CompanyRole[]>(initialRoles);
  const [groups, setGroups] = useState<CategoryGroup[]>(initialGroups);
  const [categories, setCategories] = useState<SharedCategory[]>(initialCategories);
  const [grants, setGrants] = useState<RoleGrant[]>(initialGrants);

  const [previewMemberId, setPreviewMemberId] = useState(members[2].id);
  const [draftRole, setDraftRole] = useState('');
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [draftCategory, setDraftCategory] = useState('');
  const [draftColor, setDraftColor] = useState(SWATCHES[0]);
  const [addingToGroup, setAddingToGroup] = useState<string | null>(null);
  const [draftGroup, setDraftGroup] = useState('');
  const [addGroupOpen, setAddGroupOpen] = useState(false);

  const hasGrant = (roleId: string, nodeId: string) =>
    grants.some(grant => grant.roleId === roleId && grant.nodeId === nodeId);

  const leavesOf = (groupId: string) => categories.filter(category => category.groupId === groupId);

  const groupCell = (groupId: string, roleId: string): CellState => {
    if (hasGrant(roleId, groupId)) return 'granted';
    const anyChild = leavesOf(groupId).some(
      category => !category.archived && hasGrant(roleId, category.id),
    );
    // The group still has to reach the client so the child is not orphaned.
    return anyChild ? 'implied' : 'off';
  };

  const leafCell = (categoryId: string, groupId: string, roleId: string): CellState => {
    if (hasGrant(roleId, groupId)) return 'inherited';
    return hasGrant(roleId, categoryId) ? 'granted' : 'off';
  };

  const toggleGroup = (groupId: string, roleId: string) =>
    setGrants(prev => {
      if (prev.some(grant => grant.roleId === roleId && grant.nodeId === groupId)) {
        return prev.filter(grant => !(grant.roleId === roleId && grant.nodeId === groupId));
      }
      // Granting the whole subtree makes individual child grants redundant.
      const childIds = leavesOf(groupId).map(category => category.id);
      return [
        ...prev.filter(grant => !(grant.roleId === roleId && childIds.includes(grant.nodeId))),
        { roleId, nodeId: groupId },
      ];
    });

  const toggleLeaf = (categoryId: string, groupId: string, roleId: string) => {
    if (hasGrant(roleId, groupId)) return; // inherited from the group, not independently toggleable
    setGrants(prev =>
      prev.some(grant => grant.roleId === roleId && grant.nodeId === categoryId)
        ? prev.filter(grant => !(grant.roleId === roleId && grant.nodeId === categoryId))
        : [...prev, { roleId, nodeId: categoryId }],
    );
  };

  const toggleArchived = (categoryId: string) =>
    setCategories(prev =>
      prev.map(category =>
        category.id === categoryId ? { ...category, archived: !category.archived } : category,
      ),
    );

  const addRole = () => {
    const name = draftRole.trim();
    if (!name) return;
    setRoles(prev => [...prev, { id: `role-${prev.length}`, name, isDefault: false }]);
    setDraftRole('');
    setAddRoleOpen(false);
  };

  /** A new category lands inside a group, so every role holding that group gets it at once. */
  const addCategory = (groupId: string) => {
    const name = draftCategory.trim();
    if (!name) return;
    setCategories(prev => [
      ...prev,
      { id: `cat-${prev.length}`, name, groupId, color: draftColor, archived: false },
    ]);
    setDraftCategory('');
    setAddingToGroup(null);
  };

  const addGroup = () => {
    const name = draftGroup.trim();
    if (!name) return;
    setGroups(prev => [...prev, { id: `group-${prev.length}`, name }]);
    setDraftGroup('');
    setAddGroupOpen(false);
  };

  const memberCount = (roleId: string) => memberRoles.filter(link => link.roleId === roleId).length;

  const usageOf = (categoryId: string) => {
    const rows = memberCategoryTotals.filter(
      total => total.categoryId === categoryId && total.hours > 0,
    );
    return rows.reduce((acc, row) => acc + row.hours, 0);
  };

  // What one person ends up with: the union across their roles, plus the groups that
  // have to come along so the tree stays whole.
  const preview = useMemo(() => {
    const personRoles = rolesOf(previewMemberId);
    const leaves = grantedLeaves(
      personRoles.map(role => role.id),
      grants,
      categories,
      groups,
    );
    const visible = groups
      .map(group => ({
        group,
        items: categories.filter(
          category => category.groupId === group.id && leaves.has(category.id),
        ),
      }))
      .filter(entry => entry.items.length > 0);
    return { personRoles, groups: visible };
  }, [previewMemberId, grants, categories, groups]);

  const title = (
    <div>
      <div className="text-base font-medium leading-tight">Roles</div>
      <div className="text-xs text-muted-foreground">
        {roles.length} roles · {categories.filter(category => !category.archived).length} categories
        owned by the company
      </div>
    </div>
  );

  const extra = (
    <Popover open={addRoleOpen} onOpenChange={setAddRoleOpen}>
      <PopoverTrigger asChild>
        <Button variant="primary" size="sm" className="dark:text-primary-foreground">
          <Plus />
          Add role
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3 p-3">
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Role name</label>
          <Input
            autoFocus
            value={draftRole}
            onChange={event => setDraftRole(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && addRole()}
            placeholder="Data analyst"
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          className="w-full dark:text-primary-foreground"
          disabled={!draftRole.trim()}
          onClick={addRole}
        >
          Create
        </Button>
      </PopoverContent>
    </Popover>
  );

  return (
    <PageCard title={title} extra={extra}>
      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 border-b border-border bg-card px-2 pb-2 text-left text-xs font-normal uppercase tracking-wide text-muted-foreground">
                    Category
                  </th>
                  {roles.map(role => (
                    <th key={role.id} className="w-24 border-b border-border px-1 pb-2 align-bottom">
                      <div className="flex flex-col items-center gap-0.5">
                        {role.isDefault && (
                          <span
                            title="Everyone who joins gets this role"
                            className="flex items-center gap-0.5 text-[9px] uppercase tracking-wide text-amber-600 dark:text-amber-500"
                          >
                            <Star className="h-2.5 w-2.5" />
                            default
                          </span>
                        )}
                        <span className="text-xs font-medium">{role.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {memberCount(role.id)} {memberCount(role.id) === 1 ? 'person' : 'people'}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {groups.map(group => (
                  <React.Fragment key={group.id}>
                    <tr>
                      <td className="sticky left-0 z-10 border-b border-border/60 bg-card px-2 py-1.5">
                        <span className="flex items-center gap-1.5 text-sm font-medium">
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          {group.name}
                        </span>
                      </td>
                      {roles.map(role => (
                        <td key={role.id} className="border-b border-border/60 px-1 py-1.5 text-center">
                          <Cell
                            state={groupCell(group.id, role.id)}
                            onClick={() => toggleGroup(group.id, role.id)}
                            label={`${group.name} · ${role.name}`}
                          />
                        </td>
                      ))}
                    </tr>

                    {leavesOf(group.id).map(category => {
                      const hours = usageOf(category.id);
                      return (
                        <tr key={category.id} className={cn('group', category.archived && 'opacity-50')}>
                          <td className="sticky left-0 z-10 border-b border-border/30 bg-card py-1 pl-7 pr-2">
                            <div className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                style={{ backgroundColor: category.color }}
                              />
                              <span className={cn('text-sm', category.archived && 'line-through')}>
                                {category.name}
                              </span>
                              {category.archived && (
                                <span className="text-[10px] uppercase text-muted-foreground">
                                  archived
                                </span>
                              )}
                              <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground">
                                {hours > 0 ? `${formatHours(hours)} h` : 'unused'}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleArchived(category.id)}
                                title={
                                  category.archived
                                    ? 'Publish again'
                                    : 'Archive — stops reaching apps, logged history stays'
                                }
                                className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
                              >
                                {category.archived ? (
                                  <Eye className="h-3.5 w-3.5" />
                                ) : (
                                  <EyeOff className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          </td>
                          {roles.map(role => (
                            <td key={role.id} className="border-b border-border/30 px-1 py-1 text-center">
                              {category.archived ? (
                                <span className="text-xs text-muted-foreground/40">·</span>
                              ) : (
                                <Cell
                                  state={leafCell(category.id, group.id, role.id)}
                                  onClick={() => toggleLeaf(category.id, group.id, role.id)}
                                  label={`${category.name} · ${role.name}`}
                                />
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}

                    <tr>
                      <td className="sticky left-0 z-10 border-b border-border/30 bg-card py-1 pl-7 pr-2">
                        <Popover
                          open={addingToGroup === group.id}
                          onOpenChange={open => setAddingToGroup(open ? group.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                            >
                              <Plus className="h-3 w-3" />
                              Add category
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-64 space-y-3 p-3">
                            <div className="space-y-1.5">
                              <label className="text-xs text-muted-foreground">
                                New category in {group.name}
                              </label>
                              <Input
                                autoFocus
                                value={draftCategory}
                                onChange={event => setDraftCategory(event.target.value)}
                                onKeyDown={event => event.key === 'Enter' && addCategory(group.id)}
                                placeholder="Research"
                              />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {SWATCHES.map(color => (
                                <button
                                  key={color}
                                  type="button"
                                  onClick={() => setDraftColor(color)}
                                  style={{ backgroundColor: color }}
                                  className={cn(
                                    'h-6 w-6 rounded-sm ring-offset-2 ring-offset-background transition-all',
                                    draftColor === color && 'ring-2 ring-foreground',
                                  )}
                                />
                              ))}
                            </div>
                            <p className="text-[11px] leading-snug text-muted-foreground">
                              Roles holding the whole {group.name} group pick it up immediately.
                            </p>
                            <Button
                              variant="primary"
                              size="sm"
                              className="w-full dark:text-primary-foreground"
                              disabled={!draftCategory.trim()}
                              onClick={() => addCategory(group.id)}
                            >
                              Create
                            </Button>
                          </PopoverContent>
                        </Popover>
                      </td>
                      <td colSpan={roles.length} className="border-b border-border/30" />
                    </tr>
                  </React.Fragment>
                ))}

                <tr>
                  <td className="sticky left-0 z-10 bg-card px-2 py-2">
                    <Popover open={addGroupOpen} onOpenChange={setAddGroupOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <Plus className="h-3 w-3" />
                          Add group
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-64 space-y-3 p-3">
                        <div className="space-y-1.5">
                          <label className="text-xs text-muted-foreground">Group name</label>
                          <Input
                            autoFocus
                            value={draftGroup}
                            onChange={event => setDraftGroup(event.target.value)}
                            onKeyDown={event => event.key === 'Enter' && addGroup()}
                            placeholder="Research"
                          />
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full dark:text-primary-foreground"
                          disabled={!draftGroup.trim()}
                          onClick={addGroup}
                        >
                          Create
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </td>
                  <td colSpan={roles.length} />
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-foreground">
                <Check className="h-2.5 w-2.5 text-background" />
              </span>
              granted
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-muted-foreground/30">
                <Check className="h-2.5 w-2.5 text-foreground/60" />
              </span>
              inherited from the group
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center rounded-sm border border-dashed border-border">
                <Minus className="h-2.5 w-2.5 text-muted-foreground" />
              </span>
              sent for tree structure only
            </span>
          </div>
        </div>

        {/* The union rule is easier to trust when you can look at one person's result. */}
        <aside className="min-w-0">
          <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
            <Smartphone className="h-3.5 w-3.5" />
            Preview as
          </div>

          <select
            value={previewMemberId}
            onChange={event => setPreviewMemberId(event.target.value)}
            className="mb-3 h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            {members.map(member => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>

          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="mb-2 flex flex-wrap gap-1">
              {preview.personRoles.map(role => (
                <span
                  key={role.id}
                  className="rounded-full border border-border bg-background px-1.5 py-0.5 text-[10px]"
                >
                  {role.name}
                </span>
              ))}
            </div>

            {preview.groups.length === 0 && (
              <div className="py-4 text-center text-xs text-muted-foreground">
                No categories — this person would open an empty app.
              </div>
            )}

            {preview.groups.map(({ group, items }) => (
              <div key={group.id} className="mb-3 last:mb-0">
                <div className="mb-1.5 text-[11px] text-muted-foreground">{group.name}</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {items.map(category => (
                    <div
                      key={category.id}
                      className="flex h-14 items-end rounded-md p-1.5 text-[10px] leading-tight text-white"
                      style={{ backgroundColor: category.color }}
                    >
                      <span className="line-clamp-2">{category.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
              Plus whatever people track for themselves — you never see that.
            </div>
          </div>
        </aside>
      </div>
    </PageCard>
  );
};

const Cell: React.FC<{ state: CellState; onClick: () => void; label: string }> = ({
  state,
  onClick,
  label,
}) => {
  const title =
    state === 'inherited'
      ? `${label}: comes with the whole group — remove the group grant to change it`
      : state === 'implied'
      ? `${label}: not granted, but sent so its children keep a parent`
      : label;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={state === 'inherited'}
      title={title}
      className={cn(
        'mx-auto flex h-5 w-5 items-center justify-center rounded-sm transition-colors',
        state === 'granted' && 'bg-foreground',
        state === 'inherited' && 'cursor-default bg-muted-foreground/30',
        state === 'implied' && 'border border-dashed border-border',
        state === 'off' && 'border border-border hover:border-foreground hover:bg-muted',
      )}
    >
      {state === 'granted' && <Check className="h-3 w-3 text-background" />}
      {state === 'inherited' && <Check className="h-3 w-3 text-foreground/60" />}
      {state === 'implied' && <Minus className="h-3 w-3 text-muted-foreground" />}
    </button>
  );
};

export default RolesPage;
